// Profil-szerkesztő TISZTA segédei (nem-komponens): form-értékek, a @atlas/contracts write-sémájából épített
// resolver, valamint a step-up / jelszó-policy / 2FA-állapotgép / Asana-háromállapot LEKÉPEZÉSEK. Külön fájl a
// komponenstől, hogy a react-refresh (Fast Refresh) tiszta maradjon ÉS a logika unit-tesztelhető legyen.
//
// FORRÁS-IGAZSÁG: a HTTP body sémája a @atlas/contracts-ból jön (ProfileUpdateBodySchema) — NINCS saját body-séma.
// A titok-mezők (currentPassword / newPassword / code) WRITE-ONLY: sosem töltjük vissza, sosem tároljuk a cache-ben.
import { ProfileUpdateBodySchema, type ProfileSchema } from '@atlas/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { type TFunction } from 'i18next';
import { type Resolver } from 'react-hook-form';
import { type z } from 'zod';

type Profile = z.infer<typeof ProfileSchema>;
type ProfileUpdateBody = z.infer<typeof ProfileUpdateBodySchema>;

// ── A profil-form (PATCH /profile) mező-értékei ────────────────────────────────────────────────────────────
/**
 * A profil-form string-alapú mező-értékei. A titok (currentPassword) WRITE-ONLY: mindig üresen indul, és CSAK
 * e-mail-váltáskor kerül a body-ba (step-up). Az Asana PAT és a jelszó-csere KÜLÖN endpoint → külön form (lásd lent).
 */
export interface ProfileFormValues {
  givenName: string;
  familyName: string;
  nickname: string;
  email: string;
  phone: string;
  discordId: string;
  notifyEmail: boolean;
  notifyInApp: boolean;
  /** Step-up jelszó — CSAK e-mail-váltáshoz; write-only (SOHA nem logolt/cache-elt). */
  currentPassword: string;
}

/** A GET /profile olvasott alakból → form-értékek (a null-okat üres stringre, a titkot üresre állítva). */
export function profileToFormValues(p: Profile): ProfileFormValues {
  return {
    givenName: p.givenName ?? '',
    familyName: p.familyName ?? '',
    nickname: p.nickname ?? '',
    email: p.email,
    phone: p.phone ?? '',
    discordId: p.discordId ?? '',
    notifyEmail: p.notifyEmail,
    notifyInApp: p.notifyInApp,
    currentPassword: '',
  };
}

/** E-mail-VÁLTOZÁS detektálása (case-insensitive, trimmelve) — az Odin az e-mailt kis/nagybetű-függetlenül kulcsolja. */
export function isEmailChanged(
  values: Pick<ProfileFormValues, 'email'>,
  originalEmail: string,
): boolean {
  return values.email.trim().toLowerCase() !== originalEmail.trim().toLowerCase();
}

/**
 * Form-értékek → PATCH wire-body. A profil-mezők mindig mennek (idempotens); az `email` CSAK ha VÁLTOZOTT, és
 * ekkor — ha megadták — a `currentPassword` (step-up) is. A `meta` (OCC) mindig kötelező.
 *
 * PARTNER-felületen a `discordId` NEM megy: belső mező, a partner-űrlap nem is mutatja, a szerver pedig a
 * partner-tokennel küldött értékét 422-vel elutasítja. (MÉRT HIBA: az üres stringként is elküldött mező miatt
 * a partner SEMMILYEN profil-mentése nem ment át.)
 */
export function profileFormToBody(
  v: ProfileFormValues,
  opts: {
    originalEmail: string;
    meta: { identityVersion: number; profileVersion: number };
    surface?: 'internal' | 'partner';
  },
): ProfileUpdateBody {
  const body: ProfileUpdateBody = {
    givenName: v.givenName.trim(),
    familyName: v.familyName.trim(),
    nickname: v.nickname.trim(),
    phone: v.phone.trim(),
    ...(opts.surface === 'partner' ? {} : { discordId: v.discordId.trim() }),
    notifyEmail: v.notifyEmail,
    notifyInApp: v.notifyInApp,
    meta: opts.meta,
  };
  if (isEmailChanged(v, opts.originalEmail)) {
    body.email = v.email.trim();
    if (v.currentPassword !== '') body.currentPassword = v.currentPassword;
  }
  return body;
}

/** A resolver-validáláshoz: az e-mailt MINDIG belefűzzük (fusson a `.email()`), a meta placeholder. */
function toValidationBody(v: ProfileFormValues): unknown {
  return {
    givenName: v.givenName.trim(),
    familyName: v.familyName.trim(),
    nickname: v.nickname.trim(),
    email: v.email.trim(),
    phone: v.phone.trim(),
    discordId: v.discordId.trim(),
    notifyEmail: v.notifyEmail,
    notifyInApp: v.notifyInApp,
    meta: { identityVersion: 0, profileVersion: 0 },
  };
}

/** Lokalizált zod-hibatérkép: az e-mail formátum-hibát vezeti a mezőre; egyéb → generikus. */
export function buildProfileErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    if (issue.path[0] === 'email') return { message: t('validation:profile.email') };
    return { message: ctx.defaultError };
  };
}

/**
 * RHF-resolver a contract-sémából (ProfileUpdateBodySchema) — a validálás a WIRE-alakon fut. Kiegészítve a
 * Discord-id kliens-oldali numerikus ellenőrzésével (a contract nem kötelezi, de a UI numerikus mezőt vár).
 * Sikerkor az EREDETI (string-alapú) form-értékek mennek tovább; a wire-body-t a submit építi.
 */
export function buildProfileResolver(t: TFunction): Resolver<ProfileFormValues> {
  const base = zodResolver(ProfileUpdateBodySchema, {
    errorMap: buildProfileErrorMap(t),
  }) as unknown as Resolver<ProfileFormValues>;
  return async (values, context, options) => {
    const result = await base(toValidationBody(values) as ProfileFormValues, context, options);
    const errors: Record<string, { type: string; message: string }> = {
      ...(result.errors as Record<string, { type: string; message: string }>),
    };
    const discord = values.discordId.trim();
    if (discord !== '' && !/^\d+$/.test(discord)) {
      errors.discordId = { type: 'pattern', message: t('validation:profile.discordId') };
    }
    if (Object.keys(errors).length > 0) return { values: {}, errors };
    return { values, errors: {} };
  };
}

// ── Jelszó-csere (POST /profile/password) ──────────────────────────────────────────────────────────────────
export interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const EMPTY_PASSWORD_FORM: PasswordFormValues = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

/**
 * A jelszó-policy GÉPI kódjai — a SZERVER (Odin `password-policy.ts`) autoritatív kód-szótárával AZONOSAK, hogy a
 * szerver 422-mezőhibája mezőnként találjon (különben generikusra esne). Forrás: `validatePasswordPolicy` kódjai.
 */
export type PasswordViolationCode =
  | 'required'
  | 'too_short'
  | 'too_long'
  | 'missing_lowercase'
  | 'missing_uppercase'
  | 'missing_digit'
  | 'same_as_current';

/** A minimális hossz — a policy-hint és a kliens-tükör közös forrása (a szerver `PASSWORD_MIN_LENGTH`-jével). */
export const PASSWORD_MIN_LENGTH = 12;
/** A maximális hossz — a szerver `PASSWORD_MAX_LENGTH`-jével (a `too_long` üzenethez). */
export const PASSWORD_MAX_LENGTH = 200;

/**
 * A jelszó-policy KLIENS-OLDALI tükre (a szerver forrás-igazság; ez csak azonnali visszajelzés): min. 12 karakter,
 * kis- + nagybetű + szám, és új ≠ régi. A szimbólum AJÁNLOTT (nem kötelező) → itt nem bukik el. A SZERVER-vel
 * AZONOS kódokat adja vissza (`missing_*`, `too_short`, `same_as_current`) — így a kliens- és szerver-visszajelzés
 * ugyanazt a szótárat lokalizálja.
 */
export function checkPasswordPolicy(
  newPassword: string,
  currentPassword: string,
): PasswordViolationCode[] {
  const codes: PasswordViolationCode[] = [];
  if (newPassword.length < PASSWORD_MIN_LENGTH) codes.push('too_short');
  if (!/[a-z]/.test(newPassword)) codes.push('missing_lowercase');
  if (!/[A-Z]/.test(newPassword)) codes.push('missing_uppercase');
  if (!/\d/.test(newPassword)) codes.push('missing_digit');
  if (newPassword !== '' && newPassword === currentPassword) codes.push('same_as_current');
  return codes;
}

/**
 * A szerver 422-violation kódjának → lokalizált üzenet. A kulcsok a SZERVER (Odin) minden policy-kódját fedik
 * (`required`/`too_short`/`too_long`/`missing_lowercase`/`missing_uppercase`/`missing_digit`/`same_as_current`);
 * ismeretlen kód → generikus policy-üzenet (a nyers upstream-szöveg SOHA nem szivárog).
 */
export function passwordViolationMessage(code: string, t: TFunction): string {
  const known: Record<PasswordViolationCode, string> = {
    required: t('validation:profile.password.required'),
    too_short: t('validation:profile.password.too_short', { count: PASSWORD_MIN_LENGTH }),
    too_long: t('validation:profile.password.too_long', { count: PASSWORD_MAX_LENGTH }),
    missing_lowercase: t('validation:profile.password.missing_lowercase'),
    missing_uppercase: t('validation:profile.password.missing_uppercase'),
    missing_digit: t('validation:profile.password.missing_digit'),
    same_as_current: t('validation:profile.password.same_as_current'),
  };
  return known[code as PasswordViolationCode] ?? t('validation:profile.password.generic');
}

// ── Asana PAT (PUT / DELETE /profile/asana-pat) — write-only titok ─────────────────────────────────────────
export type AsanaAction = 'set' | 'clear' | 'none';

/**
 * A három-állapotú Asana-szemantika (a munkatárs-mintával azonos, de itt KÜLÖN endpointra): nem-üres input → `set`
 * (PUT, felülír) · üres input + törlés-szándék → `clear` (DELETE) · üres input, nincs szándék → `none` (nincs hívás).
 */
export function asanaAction(input: string, clearIntent: boolean): AsanaAction {
  if (input.trim() !== '') return 'set';
  if (clearIntent) return 'clear';
  return 'none';
}

// ── 2FA (TOTP) állapotgép + step-up ────────────────────────────────────────────────────────────────────────
export type TwoFactorStatus = 'enabled' | 'disabled';

/** A szerver-jelzőkből (mfaEnabled/twoFactorPending) → a 2FA-kártya alap-állapota. Aktív ha be van kapcsolva. */
export function twoFactorStatus(p: {
  mfaEnabled: boolean;
  twoFactorPending: boolean;
}): TwoFactorStatus {
  return p.mfaEnabled ? 'enabled' : 'disabled';
}

export interface StepUpValues {
  /** TOTP vagy recovery kód. */
  code: string;
  /** Jelenlegi jelszó (alternatív step-up). */
  currentPassword: string;
}

export const EMPTY_STEP_UP: StepUpValues = { code: '', currentPassword: '' };

/** Van-e legalább egy step-up bemenet (kód VAGY jelszó)? A disable/recovery-codes ezt követeli meg. */
export function hasStepUp(v: StepUpValues): boolean {
  return v.code.trim() !== '' || v.currentPassword !== '';
}

/** A step-up wire-body: a KÓD nyer a jelszó felett; egyik sem → üres objektum (a hívó blokkolja). Write-only. */
export function stepUpBody(v: StepUpValues): { code?: string; currentPassword?: string } {
  const code = v.code.trim();
  if (code !== '') return { code };
  if (v.currentPassword !== '') return { currentPassword: v.currentPassword };
  return {};
}
